import { FC, useState } from "react";

import { serverGetCourseGrades } from "api/api-server-data";
import { CourseCodeMenu } from "components/menus/CourseCodeMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { ArrowDownIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCourses, selectCurrentOrg, selectTerms } from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { downloadCSVFile } from "utils/file";
import { generateCourseGradesCSV } from "utils/reporting";

export const StudentCourseGradesReport: FC = () => {
  const [selectedTermId, setSelectedTermId] = useState("");
  const [selectedCourseCodeId, setSelectedCourseCodeId] = useState("");
  const terms = useAppSelector(selectTerms);
  const courses = useAppSelector(selectCourses);
  const currentOrg = useAppSelector(selectCurrentOrg);

  const handleDownloadCSV = async () => {
    if (!selectedTermId) {
      toastErrorMessage(t("assessment_summary_report.select_term_to_view_data"));
      return;
    }

    try {
      const data = await serverGetCourseGrades(selectedTermId, selectedCourseCodeId || undefined);

      if (!data || data.length === 0) {
        toastErrorMessage(t("assessment_summary_report.no_data"));
        return;
      }

      const selectedTerm = terms.find((term) => term.id === selectedTermId);
      const termTitle = selectedTerm?.title || selectedTermId;

      // Filter courses to only those in the selected term
      const termCourses = courses.filter((course) => course.term_id === selectedTermId);

      const { csvData, filename } = generateCourseGradesCSV(
        data,
        termTitle,
        termCourses,
        currentOrg?.course_score_display_format,
        currentOrg?.rubric,
      );
      trackEvent("Home - Reports / Download course grades", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: filename,
      });
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  return (
    <div className="student-course-grades-view">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("student_course_grades_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            defaultToAll={false}
            direction="top"
            eventPrefix="Home - Reports / "
            menuType="menu"
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={setSelectedTermId}
          />
        </div>

        <div className="flex items-center gap-2">
          <CourseCodeMenu
            direction="top"
            menuType="menu"
            selectedCourseCodeId={selectedCourseCodeId}
            size="s"
            onCourseCodeChange={setSelectedCourseCodeId}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            disabled={!selectedTermId}
            eventName="Student Course Grades Report - Download CSV"
            kind="white"
            size="s"
            onClick={handleDownloadCSV}
          >
            <IconText
              iconEnd={<ArrowDownIcon />}
              text={t("student_course_grades_report.download_csv")}
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
